/**
 * ScriptVariantsPanel — the Script Writer's screenplay iteration history.
 *
 * Deliberately NOT the shared `VariantsTabContent`: that renders an image grid
 * (thumbnails, aspect ratios, like/dislike markers) and has nothing to show for
 * a screenplay. This is a plain newest-first list of rows, with the active
 * ("master") script outlined in the agent colour.
 *
 * Rendered inside `UnifiedBottomPanel` in `hideTabBar` mode, matching the
 * dedicated Variants panel the image nodes use.
 */

import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'

/**
 * One row in the list.
 *
 * A plain view-model rather than `ScriptVariantDictYMap`: that generated type is
 * a class wrapping a live Y.Map, so the node cannot synthesize rows for legacy
 * canvases (which have per-model results but no stored variant history) without
 * one. Mapping to this shape at the call site keeps the panel free of YDoc types.
 */
export interface ScriptVariantRow {
  id: string
  /** 1-based run number, rendered as "Script N". */
  ordinal: number
  /** Project or scene name appended after the run number, when present. */
  label: string
  /**
   * Replaces the whole "Script N – label" title. Used by the synthesized rows
   * on legacy canvases, where the row stands for a MODEL rather than a run, so
   * "Script 1" would be a made-up run number.
   */
  titleOverride?: string
  /** `data-testid` for the row. */
  testId: string
}

/** Row height + gap, used to size the panel without a scrollbar for short lists. */
const ROW_HEIGHT = 34
const ROW_GAP = 6
const PANEL_CHROME = 46
const MAX_VISIBLE_ROWS = 6

/** Panel height for a given variant count, so short lists don't leave dead space. */
export function getScriptVariantsPanelHeight(variantCount: number): number {
  const rows = Math.min(Math.max(variantCount, 1), MAX_VISIBLE_ROWS)
  return PANEL_CHROME + rows * ROW_HEIGHT + Math.max(0, rows - 1) * ROW_GAP
}

interface ScriptVariantsPanelProps {
  /** Variant history, oldest first, mirroring `out_script_variants` order. */
  variants: ScriptVariantRow[]
  /** `in_active_variant_id` — the master script, outlined in the accent colour. */
  activeVariantId: string
  /** Select a variant to display on the node. */
  onSelectVariant: (variantId: string) => void
  /** Agent colour used for the master-script outline. */
  accentColor: string
}

const ScriptVariantsPanel = memo(function ScriptVariantsPanel({
  variants,
  activeVariantId,
  onSelectVariant,
  accentColor,
}: ScriptVariantsPanelProps) {
  const { t } = useTranslation('canvas')

  // Newest first, matching the mock and every other Variants surface. The stored
  // order is oldest-first so `ordinal` stays stable as entries are appended.
  const ordered = useMemo(() => [...variants].reverse(), [variants])

  // With no explicit selection the newest entry IS the master, which is what the
  // node renders from `out_script`. Resolving it here keeps the outline in sync
  // with the node body instead of showing nothing selected on a fresh canvas.
  const resolvedActiveId = useMemo(() => {
    if (activeVariantId && ordered.some((v) => v.id === activeVariantId)) return activeVariantId
    return ordered[0]?.id ?? ''
  }, [activeVariantId, ordered])

  if (ordered.length === 0) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-4 py-6 text-xs font-medium text-text-muted"
        data-testid="script-variants-empty"
      >
        {t('scriptWriterNode.variantsEmpty')}
      </div>
    )
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-1.5 p-3" data-testid="script-variants-panel">
      <span className="shrink-0 text-xxs text-muted-foreground leading-none">
        {t('quickInfoBar.variants')}
      </span>
      <div
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto nodrag nowheel"
        onWheel={(e) => e.stopPropagation()}
      >
        {ordered.map((variant) => {
          const isActive = variant.id === resolvedActiveId
          const label = variant.label.trim()
          const name = t('scriptWriterNode.variantLabel', { number: variant.ordinal })
          const title =
            variant.titleOverride ?? (label ? `${name} – ${label}` : name)
          return (
            <button
              key={variant.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onSelectVariant(variant.id)
              }}
              aria-current={isActive || undefined}
              data-testid={variant.testId}
              data-active={isActive || undefined}
              style={{
                height: ROW_HEIGHT,
                borderColor: isActive ? accentColor : undefined,
              }}
              className={cn(
                'shrink-0 rounded-lg border px-3 text-center text-xs font-medium transition-colors nodrag',
                isActive
                  ? 'text-foreground'
                  : 'border-border-subtle text-text-secondary hover:bg-surface-2 hover:text-foreground',
              )}
            >
              <span className="truncate">{title}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
})

ScriptVariantsPanel.displayName = 'ScriptVariantsPanel'

export { ScriptVariantsPanel }
